import {
  Component,
  input,
  signal,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
  computed
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { LoadingOverlayComponent } from 'src/app/presentation/shared/loading-overlay.component';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import {
  ClienteSeleccionado,
  BusquedaClienteDialogComponent
} from 'src/app/presentation/common/busquedas/busqueda-cliente-dialog.component';
import {
  BusquedaAsesorDialogComponent,
  AsesorSeleccionado
} from 'src/app/presentation/common/busquedas/busqueda-asesor-dialog.component';
import {
  BusquedaUnidadAdministrativaDialogComponent,
  UnidadAdministrativaSeleccionada
} from 'src/app/presentation/shared/buscadores/buscador-unidad-administrativa-dialog.component';
import {
  BusquedaAseguradoraDialogComponent,
  AseguradoraSeleccionada
} from 'src/app/presentation/shared/buscadores/buscador-aseguradora-dialog.component';
import { AseguradoraRepository } from 'src/app/domain/service/aseguradora/aseguradora-repository';
import { InformeCumplimientoRepository } from 'src/app/domain/service/informe/informe-cumplimiento-repository';
import { InformeCumplimiento } from 'src/app/domain/model/informe/informe-cumplimiento';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { GestionarContactosInformeDialogComponent } from 'src/app/presentation/shared/contactos/gestionar-contactos-informe-dialog.component';
import { informesRechazadosSingleSelectionPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { DEFAULT_ROWS_PER_PAGE, MAX_OBSERVACIONES_ENVIO_INFORME } from 'src/app/common/constants-ejb';
import {
  EXTENSIONES_ACEPTADAS_INFORME,
  EXTENSIONES_PERMITIDAS_INFORME,
  MAX_FILE_SIZE_INFORME,
  esTipoArchivoInformePermitido
} from 'src/app/domain/model/cumplimiento/archivo-informe.constants';
import { DropZoneDirective } from 'src/app/presentation/shared/file-uploader/drop-zone.directive';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/**
 * Filtro de consulta de cumplimientos rechazados.
 * Mapea a los parámetros del SP PCK_SGS_INFORMES_CUMPLIMIENTOS.SP_SGS_INFORMES_PENDIENTES
 *
 * Legacy: Sp_sgs_informes_pendientesInputParameter
 */
export interface FiltroCumplimientos {
  /** Código de orden de servicio - ivacdorden */
  cdorden?: string;
  /** Consecutivo cliente (aseguradora) - inunmconsecutivocliente */
  nmconsecutivoCliente?: number;
  /** Consecutivo beneficiario - inunmconsecutivobenef */
  nmconsecutivoBeneficiario?: number;
  /** Consecutivo asesor/consultor - inunmconsecutivoasesor */
  nmconsecutivoAsesor?: number;
  /** Consecutivo oficina - inunmconsecutivooficina */
  nmconsecutivoOficina?: number;
  /** Consecutivo unidad administrativa - inunmconsunidadadmin */
  nmconsecutivoUnidadAdmin?: number;
  /** Fecha programación inicio - idefeprogramacionini */
  feProgramacionIni?: Date;
  /** Fecha programación fin - idefeprogramacionfin */
  feProgramacionFin?: Date;
  /** Riesgos programados S/N - ivasnriesgosprog */
  snRiesgosProg?: string;
}

/**
 * Formulario reutilizable de consulta de cumplimientos rechazados.
 * Compartido entre Orientaciones Programadas y No Programadas.
 *
 * Legacy: FilterCumplimProgViewImpl.java / FilterCumplimNoProgViewImpl.java
 * @author Sura - Migración GWT a Angular
 */
@Component({
  selector: 'app-consultar-cumplimientos-form',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    GestionarContactosInformeDialogComponent,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DatePickerModule,
    DropdownModule,
    CardModule,
    TooltipModule,
    TableModule,
    CheckboxModule,
    MessageModule,
    DialogModule,
    DropZoneDirective,
    RadioButtonModule,
    TextareaModule,
    BusquedaClienteDialogComponent,
    BusquedaAsesorDialogComponent,
    BusquedaUnidadAdministrativaDialogComponent,
    BusquedaAseguradoraDialogComponent,
    ConfirmDialogModule,
    LoadingOverlayComponent,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [ConfirmationService],
  template: `
    <!-- Spinner global: cubre búsqueda, envío de informe y guardado de contactos -->
    <app-loading-overlay
      [blocked]="loading() || enviandoInforme()"
    />

    <!-- Encabezado con título y botón Enviar Informe -->
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-semibold text-blue-800 dark:text-blue-300">
        Consultar Cumplimientos
      </h3>
      @if (resultados().length > 0) {
        <button
          pButton
          type="button"
          label="Enviar Informe"
          icon="pi pi-send"
          class="p-button-link text-blue-600 dark:text-blue-400"
          [disabled]="selectedInformes().length === 0"
          (click)="enviarInforme()"
        ></button>
      }
    </div>

    <p-card>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Orientación de servicio -->
        <div class="flex flex-col gap-1">
          <label
            for="cdorden"
            class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Orientación de servicio</label
          >
          <input
            pInputText
            id="cdorden"
            [(ngModel)]="form.cdorden"
            maxlength="10"
            placeholder="Código de orden"
            class="w-full"
          />
        </div>

        <!-- Fecha Inicial Programación -->
        <div class="flex flex-col gap-1">
          <label
            for="feIni"
            class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Fecha Inicial Programación</label
          >
          <p-datepicker
            id="feIni"
            [(ngModel)]="form.feProgramacionIni"
            dateFormat="dd/mm/yy"
            [showIcon]="true"
            styleClass="w-full"
          />
        </div>

        <!-- Fecha Final Programación -->
        <div class="flex flex-col gap-1">
          <label
            for="feFin"
            class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Fecha Final Programación</label
          >
          <p-datepicker
            id="feFin"
            [(ngModel)]="form.feProgramacionFin"
            dateFormat="dd/mm/yy"
            [showIcon]="true"
            styleClass="w-full"
          />
        </div>

        <!-- Aseguradora (Cliente) -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Aseguradora</label
          >
          <div class="flex gap-2 items-center">
            <input
              pInputText
              [value]="nombreAseguradora()"
              readonly
              placeholder="Seleccionar aseguradora"
              class="flex-1"
            />
            <button
              pButton
              type="button"
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar aseguradora"
              (click)="dialogBuscarAseguradoraVisible = true"
            ></button>
            @if (form.nmconsecutivoCliente) {
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-outlined p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar aseguradora"
                (click)="limpiarAseguradora()"
              ></button>
            }
          </div>
        </div>

        <!-- Beneficiario (Cliente empresa) -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Cliente</label
          >
          <div class="flex gap-2 items-center">
            <input
              pInputText
              [value]="nombreBeneficiario()"
              readonly
              placeholder="Seleccionar cliente"
              class="flex-1"
            />
            <button
              pButton
              type="button"
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar cliente"
              (click)="dialogBuscarClienteVisible = true"
            ></button>
            @if (form.nmconsecutivoBeneficiario) {
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-outlined p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar cliente"
                (click)="limpiarBeneficiario()"
              ></button>
            }
          </div>
        </div>

        <!-- Oficina -->
        <div class="flex flex-col gap-1">
          <label
            for="oficina"
            class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Oficina</label
          >
          <p-dropdown
            id="oficina"
            [options]="oficinas()"
            [(ngModel)]="form.nmconsecutivoOficina"
            optionLabel="dsoficina"
            optionValue="nmconsecutivo"
            placeholder="Seleccione una oficina"
            [showClear]="true"
            [filter]="true"
            filterBy="dsoficina"
            filterPlaceholder="Buscar oficina..."
            [resetFilterOnHide]="true"
            emptyFilterMessage="Sin coincidencias"
            styleClass="w-full"
          />
        </div>

        <!-- Unidad Administrativa -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Unidad Administrativa</label
          >
          <div class="flex gap-2 items-center">
            <input
              pInputText
              [value]="nombreUnidadAdmin()"
              readonly
              placeholder="Seleccionar unidad administrativa"
              class="flex-1"
            />
            <button
              pButton
              type="button"
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar unidad administrativa"
              (click)="dialogBuscarUnidadAdminVisible = true"
            ></button>
            @if (form.nmconsecutivoUnidadAdmin) {
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-outlined p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar unidad"
                (click)="limpiarUnidadAdmin()"
              ></button>
            }
          </div>
        </div>

        <!-- Asesor/Profesional -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Asesor / Profesional</label
          >
          <div class="flex gap-2 items-center">
            <input
              pInputText
              [value]="nombreConsultor()"
              readonly
              placeholder="Seleccionar asesor o profesional"
              class="flex-1"
            />
            <button
              pButton
              type="button"
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              pTooltip="Buscar asesor o profesional"
              (click)="dialogBuscarAsesorVisible = true"
            ></button>
            @if (form.nmconsecutivoAsesor) {
              <button
                pButton
                type="button"
                icon="pi pi-times"
                class="p-button-outlined p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar asesor o profesional"
                (click)="limpiarConsultor()"
              ></button>
            }
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="flex flex-wrap gap-3 mt-6 justify-start">
        <button
          pButton
          type="button"
          label="Limpiar"
          icon="pi pi-eraser"
          class="p-button-outlined p-button-secondary"
          (click)="limpiar()"
        ></button>
        <button
          pButton
          type="button"
          label="Buscar"
          icon="pi pi-search"
          class="p-button-primary"
          [loading]="loading()"
          (click)="buscar()"
        ></button>
      </div>
    </p-card>

    <!-- Mensaje de error -->
    @if (errorMsg()) {
      <p-message
        severity="error"
        [text]="errorMsg()"
        styleClass="mt-4 w-full"
      />
    }

    <!-- Tabla de resultados - Legacy: ListInformesViewImpl.java -->
    @if (resultados().length > 0) {
      <p-card styleClass="mt-4">
        <div class="flex justify-between items-center px-3 pt-3">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de {{ totalRegistros() }}
          </span>
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (informe of resultados(); track getInformeKey(informe)) {
            <article
              class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">Orden</div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100"
                    [attr.title]="tituloTabla(informe.cdorden)"
                  >
                    {{ textoTabla(informe.cdorden) }}
                  </div>
                </div>
                <span
                  class="text-xs font-semibold"
                  [class.text-blue-600]="esInformeSeleccionado(informe)"
                  [class.text-gray-500]="!esInformeSeleccionado(informe)"
                >
                  {{ esInformeSeleccionado(informe) ? 'Seleccionado' : 'Pendiente' }}
                </span>
              </div>

              <div class="grid grid-cols-1 gap-1 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Consultor:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsasesor) }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Aseguradora:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dscliente) }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Cliente:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsbeneficiario) }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Servicio:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsservicio) }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Tarea:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dstarea) }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Fecha:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ informe.feprogramacion | date: 'yyyy/MM/dd' }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Cantidad Ejecutada:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ informe.nmcantidadejecutada }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Estado:</span>
                  <span class="text-gray-800 dark:text-gray-200 ml-1">{{ textoTabla(informe.dsestado) }}</span>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  pButton
                  type="button"
                  label="Seleccionar"
                  icon="pi pi-check"
                  class="p-button-text p-button-sm min-h-[44px]"
                  (click)="setInformeSeleccionado(informe)"
                ></button>
                <button
                  pButton
                  type="button"
                  label="Observación"
                  icon="pi pi-eye"
                  class="p-button-text p-button-sm min-h-[44px]"
                  [disabled]="!obtenerObservacion(informe)"
                  (click)="abrirObservacion(informe)"
                ></button>
              </div>
            </article>
          }

          <div class="flex items-center justify-between gap-2 pt-1">
            <button
              pButton
              type="button"
              label="Anterior"
              icon="pi pi-angle-left"
              class="p-button-text p-button-sm"
              [disabled]="!hasPrevious()"
              (click)="irPaginaAnterior()"
            ></button>

            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ registroInicio() }} -
              {{ registroFin() }} de {{ totalRegistros() }}
            </span>

            <button
              pButton
              type="button"
              label="Siguiente"
              icon="pi pi-angle-right"
              iconPos="right"
              class="p-button-text p-button-sm"
              [disabled]="!hasNext()"
              (click)="irPaginaSiguiente()"
            ></button>
          </div>
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="informesTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [value]="resultados()"
            [(selection)]="selectedItem"
            (selectionChange)="onSelectionChange()"
            [selectionMode]="informesTableSelectionMode()"
            [paginator]="false"
            [rows]="pageSize()"
            [dataKey]="informesTableDataKey()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="
              informesTableConfig().responsive.scrollHeight || '400px'
            "
            [resizableColumns]="
              informesTableConfig().style.resizableColumns !== false
            "
            columnResizeMode="expand"
            [styleClass]="informesTableStyleClass()"
            [tableStyle]="{
              'min-width': informesTableConfig().responsive.minWidth || '1900px'
            }"
          >
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 3rem"></th>
              <th pResizableColumn pSortableColumn="cdorden">
                <span class="grid-header-text" title="Orden">Orden</span
                ><p-sortIcon field="cdorden" />
              </th>
              <th pResizableColumn pSortableColumn="dsasesor">
                <span class="grid-header-text" title="Consultor">Consultor</span
                ><p-sortIcon field="dsasesor" />
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Aseguradora"
                  >Aseguradora</span
                >
              </th>
              <th pResizableColumn pSortableColumn="dscliente">
                <span class="grid-header-text" title="Cliente">Cliente</span
                ><p-sortIcon field="dscliente" />
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Servicio">Servicio</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Tarea">Tarea</span>
              </th>
              <th pResizableColumn pSortableColumn="feprogramacion">
                <span class="grid-header-text" title="Fecha Programación"
                  >Fecha Programación</span
                ><p-sortIcon field="feprogramacion" />
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Cantidad Ejecutada"
                  >Cantidad Ejecutada</span
                >
              </th>
              <th pResizableColumn pSortableColumn="dsestado">
                <span class="grid-header-text" title="Estado">Estado</span
                ><p-sortIcon field="dsestado" />
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Observación"
                  >Observ.</span
                >
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-informe>
            <tr>
              <td>
                <p-tableRadioButton [value]="informe" />
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.cdorden)"
                  >{{ textoTabla(informe.cdorden) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dsasesor)"
                  >{{ textoTabla(informe.dsasesor) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dscliente)"
                  >{{ textoTabla(informe.dscliente) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dsbeneficiario)"
                  >{{ textoTabla(informe.dsbeneficiario) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dsservicio)"
                  >{{ textoTabla(informe.dsservicio) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dstarea)"
                  >{{ textoTabla(informe.dstarea) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    tituloTabla(informe.feprogramacion | date: 'yyyy/MM/dd')
                  "
                  >{{ informe.feprogramacion | date: 'yyyy/MM/dd' }}</span
                >
              </td>
              <td class="text-center">{{ informe.nmcantidadejecutada }}</td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(informe.dsestado)"
                  >{{ textoTabla(informe.dsestado) }}</span
                >
              </td>
              <td>
                <button
                  pButton
                  type="button"
                  icon="pi pi-eye"
                  class="p-button-text p-button-sm p-0 text-blue-600 dark:text-blue-400 min-w-[44px] min-h-[44px]"
                  [pTooltip]="
                    obtenerObservacion(informe)
                      ? 'Ver observación'
                      : 'Sin observación'
                  "
                  tooltipPosition="left"
                  (click)="abrirObservacion(informe)"
                ></button>
              </td>
            </tr>
          </ng-template>
          </p-table>

          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize()"
            (pageSizeChange)="onSelectedPageSizeChange($event)"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          />
        </div>
      </p-card>
    }

    <p-dialog
      header="Enviar Informe"
      [(visible)]="dialogEnviarInformeVisible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '34rem' }"
      [closable]="true"
      [draggable]="false"
    >
      <div class="flex flex-col gap-4 pt-2">
        <!-- Archivo (requerido) -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Archivo <span class="text-red-500">*</span></label
          >
          <input
            #fileInput
            type="file"
            class="hidden"
            [accept]="extensionesAceptadas"
            (change)="onArchivoInputChange($event)"
          />
          <div
            appDropZone
            [dropZoneDisabled]="enviandoInforme()"
            (archivoSoltado)="onArchivoSeleccionado({ files: [$event] })"
            class="flex flex-wrap items-center gap-2 p-2 w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
          >
            <button
              pButton
              type="button"
              label="Seleccionar archivo"
              icon="pi pi-upload"
              class="p-button-outlined min-h-[44px]"
              (click)="fileInput.click()"
            ></button>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              o arrastre el archivo aquí
            </span>
          </div>
          @if (archivoSeleccionado()) {
            <small class="text-gray-600 dark:text-gray-400"
              >Archivo seleccionado: {{ archivoSeleccionado()?.name }}</small
            >
          }
        </div>

        <!-- Observaciones (opcional, max 200) -->
        <div class="flex flex-col gap-1">
          <label
            for="observaciones"
            class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Observaciones</label
          >
          <textarea
            pInputTextarea
            id="observaciones"
            [(ngModel)]="envioForm.observaciones"
            (input)="envioForm.observaciones = sanitizeTextarea(envioForm.observaciones)"
            [appContadorCaracteres]="maxObservacionesEnvio"
            rows="4"
            class="w-full"
            placeholder="Ingrese observaciones (máximo 200 caracteres)"
          ></textarea>
        </div>

        <!-- Tipo de Informe (requerido) -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >Tipo de Informe <span class="text-red-500">*</span></label
          >
          <div class="flex gap-4 mt-1">
            <div class="flex items-center gap-2">
              <p-radioButton
                name="tipoInforme"
                value="P"
                [(ngModel)]="envioForm.tipoInforme"
                inputId="tipoParcial"
              />
              <label
                for="tipoParcial"
                class="text-sm text-gray-700 dark:text-gray-300"
                >Parcial</label
              >
            </div>
            <div class="flex items-center gap-2">
              <p-radioButton
                name="tipoInforme"
                value="F"
                [(ngModel)]="envioForm.tipoInforme"
                inputId="tipoFinal"
              />
              <label
                for="tipoFinal"
                class="text-sm text-gray-700 dark:text-gray-300"
                >Final</label
              >
            </div>
          </div>
        </div>

        <!-- Mail cuando aprueben informe -->
        <div class="flex items-center gap-2">
          <p-checkbox
            [(ngModel)]="envioForm.mailAprobacion"
            [binary]="true"
            inputId="mailAprobacion"
          />
          <label
            for="mailAprobacion"
            class="text-sm text-gray-700 dark:text-gray-300"
            >Mail cuando aprueben informe</label
          >
        </div>

        @if (envioErrorMsg()) {
          <p-message
            severity="error"
            [text]="envioErrorMsg()"
            styleClass="w-full"
          />
        }
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <button
            pButton
            type="button"
            label="Regresar"
            icon="pi pi-arrow-left"
            class="p-button-outlined p-button-secondary"
            (click)="cerrarModalEnvio()"
          ></button>
          <button
            pButton
            type="button"
            label="Cargar Informes"
            icon="pi pi-upload"
            class="p-button-primary"
            [loading]="enviandoInforme()"
            (click)="confirmarEnvioInforme()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>

    <p-dialog
      header="Observación de rechazo"
      [(visible)]="dialogObservacionVisible"
      [modal]="true"
      [style]="{ width: '600px', maxWidth: '95vw' }"
      [closable]="true"
      [draggable]="false"
      (onHide)="cerrarObservacion()"
    >
      <div class="flex flex-col gap-3 pt-2">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-300"
          >Observación</label
        >
        <textarea
          pInputTextarea
          [value]="observacionSeleccionada"
          readonly
          rows="8"
          class="w-full"
          placeholder="Sin observación registrada"
        ></textarea>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            (onClick)="cerrarObservacion()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Diálogos de búsqueda -->
    @if (dialogBuscarAseguradoraVisible) {
      <app-busqueda-aseguradora-dialog
        (aseguradoraSelected)="onAseguradoraSeleccionada($event)"
        (dialogClosed)="dialogBuscarAseguradoraVisible = false"
      />
    }

    @if (dialogBuscarClienteVisible) {
      <app-busqueda-cliente-dialog
        (onSelect)="onClienteSeleccionado($event)"
        (onClose)="dialogBuscarClienteVisible = false"
      />
    }

    @if (dialogBuscarUnidadAdminVisible) {
      <app-busqueda-unidad-administrativa-dialog
        [nmconsecutivoCliente]="form.nmconsecutivoCliente"
        (unidadAdministrativaSelected)="onUnidadAdminSeleccionada($event)"
        (dialogClosed)="dialogBuscarUnidadAdminVisible = false"
      />
    }

    @if (dialogBuscarAsesorVisible) {
      <app-asesor-search
        (onSelect)="onAsesorSeleccionado($event)"
        (onClose)="dialogBuscarAsesorVisible = false"
      />
    }

    <p-confirmDialog key="parcial" [style]="{ width: '92vw', maxWidth: '26rem' }" />

    <!-- Modal Gestionar Contactos del informe (componente reutilizable) -->
    <app-gestionar-contactos-informe-dialog #contactosInforme />
  `
})
export class ConsultarCumplimientosFormComponent implements OnInit {
  readonly maxObservacionesEnvio = MAX_OBSERVACIONES_ENVIO_INFORME;
  /** Tipo de orientación: PROGRAMADA | NO_PROGRAMADA */
  tipo = input.required<'PROGRAMADA' | 'NO_PROGRAMADA'>();

  // Sanitización XSS/SQL para campos de texto libre (skill seguridad-girop §3)
  sanitizeTextarea = sanitizeTextarea;

  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly informeRepository = inject(InformeCumplimientoRepository);
  private readonly aseguradoraRepository = inject(AseguradoraRepository);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);

  // Estado del formulario
  form: FiltroCumplimientos = {};

  // Oficinas para dropdown
  oficinas = signal<Oficina[]>([]);

  // Resultados de la búsqueda
  resultados = signal<InformeCumplimiento[]>([]);
  loading = signal(false);
  errorMsg = signal('');

  // Selección de filas para enviar informe
  selectedItem: InformeCumplimiento | null = null;
  selectedItems: InformeCumplimiento[] = [];
  selectedInformes = signal<InformeCumplimiento[]>([]);

  readonly informesTableConfig = computed(() =>
    informesRechazadosSingleSelectionPreset.createConfig({
      data: this.resultados() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-informes-rechazados',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      selection: {
        mode: 'single',
        selected: this.selectedItem as unknown as Record<
          string,
          unknown
        > | null,
        dataKey: 'rowKey'
      },
      pagination: {
        enabled: true,
        mode: 'server',
        pageSize: this.pageSize(),
        pageSizeOptions: this.pageSizeOptions,
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'La búsqueda no arrojó resultados.'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '1900px',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de cumplimientos rechazados',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly informesTableStyleClass = computed(() => {
    const config = this.informesTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly informesTableSelectionMode = computed<'single' | 'multiple' | null>(
    () => {
      const mode = this.informesTableConfig().selection.mode;
      return mode === 'single' || mode === 'multiple' ? mode : null;
    }
  );

  readonly informesTableDataKey = computed(() =>
    String(this.informesTableConfig().selection.dataKey || 'rowKey')
  );

  // === Paginación de servidor (ver skill frontend-girop §17) ===
  // El SP_SGS_INFORMES_PENDIENTES pagina con inudesde/inuhasta: cada búsqueda trae
  // sólo la página pedida (no un máximo fijo paginado/truncado en cliente).
  readonly pageSizeOptions = [5, 10, 20];

  readonly paginaActual = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly totalRegistros = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);

  readonly selectedPageSize = computed(() => this.pageSize());

  readonly totalPaginas = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) {
      return 0;
    }
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    if (this.totalRegistros() === 0) {
      return 0;
    }
    return Math.min(this.paginaActual() * this.pageSize(), this.totalRegistros());
  });

  // Nombres mostrados para campos de búsqueda
  nombreAseguradora = signal('');
  nombreBeneficiario = signal('');
  nombreUnidadAdmin = signal('');
  nombreConsultor = signal('');

  // Visibilidad de diálogos
  dialogBuscarAseguradoraVisible = false;
  dialogBuscarClienteVisible = false;
  dialogBuscarUnidadAdminVisible = false;
  dialogBuscarAsesorVisible = false;
  dialogObservacionVisible = false;
  observacionSeleccionada = '';

  /** Aseguradora por defecto del usuario (preservada al limpiar filtros) */
  private aseguradoraPrecargada: {
    nmconsecutivo: number;
    nombreComercial: string;
  } | null = null;

  async ngOnInit(): Promise<void> {
    try {
      const oficinas = await this.oficinaRepository.obtenerOficinas();
      this.oficinas.set(oficinas);
    } catch (error) {
      console.error('Error cargando oficinas:', error);
    }
    await this.cargarAseguradoraDefault();
  }

  /**
   * Precarga la aseguradora por defecto asociada al usuario logueado.
   * Delegado a AseguradoraRepository.obtenerAseguradoraDefault() para
   * centralizar la lógica (lectura del parámetro CLIENTE_DEFAULT_CIA y
   * resolución por NIT) y evitar duplicación entre pantallas.
   *
   * Legacy: ApplicationCommonData.searchDefaultClient()
   */
  private async cargarAseguradoraDefault(): Promise<void> {
    const aseguradora =
      await this.aseguradoraRepository.obtenerAseguradoraDefault();
    if (!aseguradora) {
      return;
    }

    const nombre =
      aseguradora.nombreComercial || aseguradora.primerNombre || '';
    this.aseguradoraPrecargada = {
      nmconsecutivo: aseguradora.nmconsecutivo,
      nombreComercial: nombre
    };
    this.form.nmconsecutivoCliente = aseguradora.nmconsecutivo;
    this.nombreAseguradora.set(nombre);
  }

  // === Handlers de selección de buscadores ===

  onAseguradoraSeleccionada(aseg: AseguradoraSeleccionada): void {
    this.form.nmconsecutivoCliente = aseg.nmconsecutivo;
    this.nombreAseguradora.set(aseg.nombreComercial);
    this.dialogBuscarAseguradoraVisible = false;
  }

  onClienteSeleccionado(cliente: ClienteSeleccionado): void {
    this.form.nmconsecutivoBeneficiario = cliente.nmconsecutivo;
    this.nombreBeneficiario.set(cliente.nombreComercial);
    this.dialogBuscarClienteVisible = false;
  }

  onUnidadAdminSeleccionada(unidad: UnidadAdministrativaSeleccionada): void {
    this.form.nmconsecutivoUnidadAdmin = unidad.nmconsecutivo;
    this.nombreUnidadAdmin.set(unidad.dsunidadAdministrativa);
    this.dialogBuscarUnidadAdminVisible = false;
  }

  onAsesorSeleccionado(asesor: AsesorSeleccionado): void {
    // Legacy usa NMCONSECUTIVO del empleado para inunmconsecutivoasesor en el SP.
    this.form.nmconsecutivoAsesor = asesor.nmconsecutivo ?? asesor.cdpersona;
    this.nombreConsultor.set(this.formatearNombreAsesor(asesor));
    this.dialogBuscarAsesorVisible = false;
  }

  abrirObservacion(informe: InformeCumplimiento): void {
    this.observacionSeleccionada = this.obtenerObservacion(informe);
    this.dialogObservacionVisible = true;
  }

  cerrarObservacion(): void {
    this.dialogObservacionVisible = false;
    this.observacionSeleccionada = '';
  }

  private formatearNombreAsesor(asesor: AsesorSeleccionado): string {
    return (
      asesor.dsnombreComercial?.trim() ||
      [
        asesor.dsnombre1,
        asesor.dsnombre2,
        asesor.dsapellido1,
        asesor.dsapellido2
      ]
        .filter((parte) => parte && parte.trim())
        .map((parte) => parte.trim())
        .join(' ') ||
      asesor.nmidentificacion
    );
  }

  obtenerObservacion(informe: InformeCumplimiento): string {
    return (
      informe.dsobservacionrechazo?.trim() ||
      informe.dsobservaciones?.trim() ||
      ''
    );
  }

  // === Limpiar campos individuales ===

  limpiarAseguradora(): void {
    this.form.nmconsecutivoCliente = undefined;
    this.nombreAseguradora.set('');
  }

  limpiarBeneficiario(): void {
    this.form.nmconsecutivoBeneficiario = undefined;
    this.nombreBeneficiario.set('');
  }

  limpiarUnidadAdmin(): void {
    this.form.nmconsecutivoUnidadAdmin = undefined;
    this.nombreUnidadAdmin.set('');
  }

  limpiarConsultor(): void {
    this.form.nmconsecutivoAsesor = undefined;
    this.nombreConsultor.set('');
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  // === Acciones principales ===

  limpiar(): void {
    this.form = {};
    this.nombreAseguradora.set('');
    this.nombreBeneficiario.set('');
    this.nombreUnidadAdmin.set('');
    this.nombreConsultor.set('');
    this.resultados.set([]);
    this.selectedItem = null;
    this.selectedItems = [];
    this.selectedInformes.set([]);
    this.resetPaginacion();
    this.errorMsg.set('');

    // Restaurar aseguradora por defecto del usuario
    if (this.aseguradoraPrecargada) {
      this.form.nmconsecutivoCliente = this.aseguradoraPrecargada.nmconsecutivo;
      this.nombreAseguradora.set(this.aseguradoraPrecargada.nombreComercial);
    }
  }

  private resetPaginacion(): void {
    this.paginaActual.set(1);
    this.totalRegistros.set(0);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
  }

  async buscar(): Promise<void> {
    // Setear snRiesgosProg según tipo
    this.form.snRiesgosProg = this.tipo() === 'PROGRAMADA' ? 'S' : 'N';
    this.selectedItem = null;
    this.selectedItems = [];
    this.selectedInformes.set([]);
    this.paginaActual.set(1);
    await this.cargarPagina(0);
  }

  /**
   * Ejecuta la búsqueda contra el backend para la ventana [start, start+pageSize)
   * y actualiza el estado de paginación con la respuesta (paginación de servidor,
   * ver skill frontend-girop §17 y acceso-datos-oracle — SP_SGS_INFORMES_PENDIENTES).
   */
  private async cargarPagina(start: number): Promise<void> {
    this.loading.set(true);
    this.errorMsg.set('');

    try {
      const size = this.pageSize();
      const page = Math.floor(start / size);
      const resultado = await this.informeRepository.consultarPendientes(
        this.form,
        page,
        size
      );
      // PrimeNG requiere un dataKey único por fila para evitar que la selección
      // de un checkbox impacte otros registros con IDs repetidos o nulos.
      const conRowKey = resultado.content.map((informe, idx) => ({
        ...informe,
        rowKey: `i${informe.nmconsecutivoinforme ?? 'x'}-c${informe.nmconsecutivocumplimiento ?? 'x'}-${idx}`
      }));
      this.resultados.set(conRowKey);
      this.totalRegistros.set(resultado.totalElements);
      this.paginaActual.set(resultado.pageNumber + 1);
      this.hasPrevious.set(!resultado.first);
      this.hasNext.set(!resultado.last);
      if (conRowKey.length === 0) {
        this.errorMsg.set('La búsqueda no arrojó resultados.');
      }
    } catch (error) {
      console.error('Error consultando informes pendientes:', error);
      this.errorMsg.set('Error al consultar informes. Intente nuevamente.');
      this.resultados.set([]);
      this.resetPaginacion();
    } finally {
      this.loading.set(false);
    }
  }

  async irPrimeraPagina(): Promise<void> {
    await this.cargarPagina(0);
  }

  async irPaginaAnterior(): Promise<void> {
    if (this.hasPrevious()) {
      await this.cargarPagina((this.paginaActual() - 2) * this.pageSize());
    }
  }

  async irPaginaSiguiente(): Promise<void> {
    if (this.hasNext()) {
      await this.cargarPagina(this.paginaActual() * this.pageSize());
    }
  }

  async irUltimaPagina(): Promise<void> {
    const ultimaPagina = this.totalPaginas();
    if (ultimaPagina > 0) {
      await this.cargarPagina((ultimaPagina - 1) * this.pageSize());
    }
  }

  async onSelectedPageSizeChange(size: number): Promise<void> {
    this.pageSize.set(size);
    this.paginaActual.set(1);
    await this.cargarPagina(0);
  }

  onSelectionChange(): void {
    this.selectedItems = this.selectedItem ? [this.selectedItem] : [];
    this.selectedInformes.set([...this.selectedItems]);
  }

  setInformeSeleccionado(informe: InformeCumplimiento): void {
    this.selectedItem = informe;
    this.onSelectionChange();
  }

  esInformeSeleccionado(informe: InformeCumplimiento): boolean {
    if (!this.selectedItem) {
      return false;
    }
    return this.getInformeKey(this.selectedItem) === this.getInformeKey(informe);
  }

  getInformeKey(informe: InformeCumplimiento): string {
    const rowKey =
      (informe as unknown as Record<string, unknown>)['rowKey'] ?? null;
    if (rowKey) {
      return String(rowKey);
    }
    return `${informe.nmconsecutivoinforme ?? 'x'}-${informe.nmconsecutivocumplimiento ?? 'x'}`;
  }

  enviarInforme(): void {
    const seleccionados = this.selectedItems;
    if (seleccionados.length === 0) {
      return;
    }

    // Abrir modal de envío
    this.envioForm = {
      observaciones: '',
      tipoInforme: '',
      mailAprobacion: false
    };
    this.archivoSeleccionado.set(null);
    this.envioErrorMsg.set('');
    this.dialogEnviarInformeVisible = true;
  }

  // === Modal Enviar Informe ===

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('contactosInforme') contactosInforme?: GestionarContactosInformeDialogComponent;

  dialogEnviarInformeVisible = false;
  enviandoInforme = signal(false);
  envioErrorMsg = signal('');
  archivoSeleccionado = signal<File | null>(null);

  envioForm: {
    observaciones: string;
    tipoInforme: string;
    mailAprobacion: boolean;
  } = { observaciones: '', tipoInforme: '', mailAprobacion: false };

  // Lista blanca alineada con backend (ArchivoInformeValidator): mismo endpoint
  // /enviar que SeccionEnviarInformeComponent, así que debe aceptar los mismos tipos.
  readonly extensionesAceptadas = EXTENSIONES_ACEPTADAS_INFORME;

  /** Selección por clic: entrega el archivo al mismo flujo que el arrastre. */
  onArchivoInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onArchivoSeleccionado({ files: Array.from(input.files ?? []) });
  }

  /**
   * Valida tipo y tamaño (clic o arrastre). El tamaño antes lo filtraba sólo
   * p-fileUpload [maxFileSize]; ahora usa el mismo límite que SeccionEnviarInforme.
   */
  onArchivoSeleccionado(event: { files: File[] }): void {
    if (event.files?.length > 0) {
      const archivo = event.files[0];
      if (!esTipoArchivoInformePermitido(archivo)) {
        this.envioErrorMsg.set(
          `Tipo de archivo no permitido. Tipos válidos: ${EXTENSIONES_PERMITIDAS_INFORME}.`
        );
        this.archivoSeleccionado.set(null);
        this.limpiarInputArchivo();
        return;
      }
      if (archivo.size > MAX_FILE_SIZE_INFORME) {
        this.envioErrorMsg.set('El archivo excede el tamaño máximo de 10 MB');
        this.archivoSeleccionado.set(null);
        this.limpiarInputArchivo();
        return;
      }
      this.envioErrorMsg.set('');
      this.archivoSeleccionado.set(archivo);
    }
  }

  private limpiarInputArchivo(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  cerrarModalEnvio(): void {
    this.dialogEnviarInformeVisible = false;
    this.envioErrorMsg.set('');
    this.archivoSeleccionado.set(null);
    this.limpiarInputArchivo();
  }

  confirmarEnvioInforme(): void {
    // Validar campos requeridos
    if (!this.archivoSeleccionado()) {
      this.envioErrorMsg.set('Debe seleccionar un archivo.');
      return;
    }
    if (!this.envioForm.tipoInforme) {
      this.envioErrorMsg.set('Debe seleccionar el tipo de informe.');
      return;
    }
    const informesSeleccionados = this.selectedInformes();
    if (informesSeleccionados.length === 0) {
      this.envioErrorMsg.set('Debe seleccionar al menos un cumplimiento.');
      return;
    }

    // Legacy: EnviarInformeViewImpl.java línea 300-322 - confirmación parcial
    if (this.envioForm.tipoInforme === 'P') {
      this.confirmationService.confirm({
        key: 'parcial',
        message:
          'Recuerde que al marcar el informe como parcial, y éste se apruebe, no será visto por la empresa y no quedará copia de él en P8. ¿Desea grabar el informe como PARCIAL?',
        header: 'Informe Parcial',
        icon: 'pi pi-info-circle',
        acceptLabel: 'Sí, continuar',
        rejectLabel: 'No',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptButtonStyleClass: 'p-button-warning p-button-sm',
        rejectButtonStyleClass: 'p-button-outlined p-button-sm',
        accept: () => this.ejecutarEnvioInforme(informesSeleccionados)
      });
    } else {
      this.ejecutarEnvioInforme(informesSeleccionados);
    }
  }

  private ejecutarEnvioInforme(
    informesSeleccionados: InformeCumplimiento[]
  ): void {
    this.envioErrorMsg.set('');
    this.enviandoInforme.set(true);

    const archivo = this.archivoSeleccionado();
    if (!archivo) {
      this.envioErrorMsg.set('Debe seleccionar un archivo.');
      this.enviandoInforme.set(false);
      return;
    }

    const payload = {
      observaciones: this.envioForm.observaciones ?? '',
      tipoInforme: this.envioForm.tipoInforme as 'P' | 'F',
      mailAprobacion: this.envioForm.mailAprobacion,
      informes: informesSeleccionados
    };

    this.informeRepository
      .enviarInforme(payload, archivo)
      .then((resultado) => {
        if (resultado.exito) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: resultado.mensaje || 'Informe enviado correctamente.'
          });
          this.cerrarModalEnvio();
          this.buscar();
          // Legacy: si es Final, abrir modal de contactos
          if (payload.tipoInforme === 'F' && resultado.nmRadicado) {
            void this.contactosInforme?.abrir(
              parseInt(resultado.nmRadicado, 10),
              informesSeleccionados.map((i) => i.nmconsecutivocumplimiento).join(',')
            );
          }
        } else {
          this.envioErrorMsg.set(
            resultado.mensaje || 'No fue posible enviar el informe.'
          );
        }
      })
      .catch((err) => {
        console.error('Error enviando informe', err);
        const mensajeServidor =
          err?.error?.message ||
          err?.message ||
          'Error de red enviando el informe.';
        this.envioErrorMsg.set(mensajeServidor);
      })
      .finally(() => this.enviandoInforme.set(false));
  }

}
